import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { NavLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  deleteIncident,
  getIncidentByMissionId,
} from '../../redux/incidents/incidentActions'
import { AppDispatch, RootState, useAppDispatch } from '../../redux/store'
import { formatTitle } from '../../utils/formatTitle'
import { systemLabel } from '../../utils/systemLabel'
import { Incident } from '../../types/incidentTypes'
import {
  DetailEntityPanel,
  DetailTable,
} from '../../components/common/detail'
import {
  canManageMission,
  DetailRowActions,
} from '../../components/common/detail/DetailRowActions'
import {
  ListPagePill,
  PRIORITY_PILL,
} from '../../components/common/ListPageCells'
import { ListPageDescription } from '../../components/common/ListPageDescription'
import { priorityLabelsTranslations } from '../../utils/constants'
import '../../components/common/ListPageTable.css'

type Props = {
  MissionId: string
  onCountChange?: (count: number) => void
  onSummaryChange?: (summary: {
    count: number
    criticalCount: number
    topLabel?: string
  }) => void
}

function asIncidentRows(raw: unknown): Incident[] {
  if (!raw) return []
  if (Array.isArray(raw)) {
    return raw.filter((i): i is Incident => Boolean(i && (i as Incident)._id))
  }
  if (typeof raw === 'object' && Array.isArray((raw as any).incidents)) {
    return ((raw as any).incidents as Incident[]).filter((i) => i && i._id)
  }
  return Object.values(raw as object).filter(
    (item): item is Incident =>
      Boolean(item && typeof item === 'object' && (item as Incident)._id)
  )
}

const IncidentListByMission: React.FC<Props> = ({
  MissionId,
  onCountChange,
  onSummaryChange,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch: AppDispatch = useAppDispatch()
  const roles = useSelector((state: any) => state.auth.roles)
  const loading = useSelector((state: RootState) => state.incident.loading)
  const incidentsList = useSelector(
    (state: RootState) => state.incident.incidents
  )
  const [search, setSearch] = useState('')
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    if (MissionId) {
      setLoaded(false)
      dispatch(getIncidentByMissionId(MissionId)).finally(() => setLoaded(true))
    }
  }, [dispatch, MissionId])

  const all = useMemo(() => asIncidentRows(incidentsList), [incidentsList])

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return all
    return all.filter((i) =>
      (i.IncidentName || '').toLowerCase().includes(q)
    )
  }, [all, search])

  useEffect(() => {
    onCountChange?.(all.length)
    const critical = all.filter((i) => Number(i.Criticality) === 1)
    const top = critical[0] || all[0]
    const bits = [
      top
        ? t(`priority.${Number(top.Criticality)}`, {
            defaultValue:
              priorityLabelsTranslations[Number(top.Criticality)] ||
              PRIORITY_PILL[Number(top.Criticality)]?.label,
          })
        : null,
      top?.Perimeter,
      top?.Type,
    ].filter(Boolean)
    onSummaryChange?.({
      count: all.length,
      criticalCount: critical.length,
      topLabel: bits.length ? bits.join(' · ') : undefined,
    })
  }, [onCountChange, onSummaryChange, all, t])

  const canAdd = canManageMission(roles)

  return (
    <DetailEntityPanel
      listTitle={t('missionDetailsPage.sections.incidentList', {
        defaultValue: 'Incidents',
      })}
      searchPlaceholder={t('filterByName', { defaultValue: 'Filter by name' })}
      searchValue={search}
      onSearchChange={setSearch}
      addLabel={t('buttons.addIncident', { defaultValue: 'Add New Incident' })}
      onAdd={() => navigate('/addincident')}
      canAdd={canAdd}
    >
      <DetailTable<Incident>
        loading={loading && !loaded}
        loadingLabel={t('loading')}
        emptyTitle={t('missionDetailsPage.empty.title', {
          defaultValue: 'Nothing here yet',
        })}
        emptyMessage={t('missionDetailsPage.empty.incidents', {
          defaultValue: 'No incident is linked to this mission yet.',
        })}
        rows={rows}
        rowKey={(r) => r._id as string}
        columns={[
          {
            key: 'name',
            header: t('Name'),
            render: (r) => (
              <NavLink
                to={`/incident-detail/${r._id}`}
                className="incyia-name-link"
              >
                <ListPageDescription
                  value={
                    r.IncidentName ? formatTitle(r.IncidentName) : undefined
                  }
                  tone="primary"
                />
              </NavLink>
            ),
          },
          {
            key: 'crit',
            header: t('Criticality', { defaultValue: 'Criticality' }),
            render: (r) => {
              const crit = Number(r.Criticality)
              const style = PRIORITY_PILL[crit] ?? PRIORITY_PILL[4]
              return (
                <ListPagePill
                  label={
                    t(`priority.${crit}`, {
                      defaultValue:
                        priorityLabelsTranslations[crit] || style.label,
                    }) || style.label
                  }
                  bg={style.bg}
                  color={style.color}
                />
              )
            },
          },
          {
            key: 'type',
            header: t('Type'),
            render: (r) => (
              <ListPageDescription
                value={
                  r.Type ? systemLabel(t, 'incidentType', r.Type) : undefined
                }
              />
            ),
          },
          {
            key: 'vuln',
            header: t('Vulnerability', { defaultValue: 'Vulnerability' }),
            render: (r) => (
              <span className="incyia-muted" style={{ fontFamily: 'monospace', fontSize: 12.5 }}>
                {r.Vulnerability || '—'}
              </span>
            ),
          },
          {
            key: 'sla',
            header: t('SLA'),
            render: (r) => (
              <span style={{ fontFamily: 'monospace' }}>
                {r.SLA != null
                  ? `${r.SLA} ${t('listPages.days', { count: Number(r.SLA) })}`
                  : '—'}
              </span>
            ),
          },
        ]}
        actionsHeader={t('Actions', { defaultValue: 'Action' })}
        renderActions={(r) => (
          <DetailRowActions
            roles={roles}
            onEdit={() => navigate(`/incident/${r._id}`)}
            onDelete={() => dispatch(deleteIncident(r._id as string))}
            deleteItem={{
              entityType: t('incidents', { defaultValue: 'Incident' }),
              name: r.IncidentName
                ? formatTitle(r.IncidentName)
                : undefined,
              details: [
                {
                  label: t('deleteConfirm.fields.mission'),
                  value: r.MissionName
                    ? formatTitle(r.MissionName)
                    : '—',
                },
                {
                  label: t('deleteConfirm.fields.status'),
                  value: (r as Incident & { Status?: string }).Status || '—',
                },
              ],
            }}
          />
        )}
      />
    </DetailEntityPanel>
  )
}

export default IncidentListByMission
